import { DEFAULT_DATE_SUBMIT_FORMAT, dateOnly, now } from "shared/utils/dayjs";
import { InstructorAvailability } from "shared/types";

export type ImpossibleDateReason = "no_working_hours" | "on_leave";

export interface ImpossibleDate {
  date: string; // YYYY-MM-DD
  reason: ImpossibleDateReason;
}

export interface AvailabilityDisabledTime {
  disabledHours: () => number[];
  disabledMinutes: (hour: number) => number[];
}

export interface AvailabilityDisabledTimeResult {
  disabledTime: () => AvailabilityDisabledTime;
  // Dates that are impossible regardless of the chosen time (a weekday the
  // instructor never works, or a date fully covered by approved leave) —
  // what lets the "no valid time" message name a real day instead of
  // staying generic. See TimeSection.tsx's Alert.
  impossibleDates: ImpossibleDate[];
}

const MINUTE_STEP = 5;
const MINUTES_IN_DAY = 24 * 60;

const hhmmToMinutes = (hhmm: string): number => {
  const [h, m] = hhmm.split(":").map(Number);
  return h * 60 + m;
};

const EMPTY_RESULT: AvailabilityDisabledTimeResult = {
  disabledTime: () => ({ disabledHours: () => [], disabledMinutes: () => [] }),
  impossibleDates: [],
};

/**
 * Core computation, parameterized on an explicit date list rather than
 * always reading availability.dates directly — reused both by the "valid
 * across the whole pattern" aggregate below and by
 * buildAvailabilityDisabledTimeForWeekday's narrowed, single-weekday view
 * (for the per-day "Waktu berbeda per hari" pickers), so both share
 * identical validity logic instead of two hand-copied versions drifting
 * apart. Mirrors assertInstructorAvailable's three rules exactly — same
 * inclusive comparisons for working-hour blocks, same strict-overlap check
 * for busy windows (`start < otherEnd && end > otherStart`), same
 * three-state working-hours semantic — just evaluated client-side against
 * pre-fetched data instead of one network round-trip per candidate time.
 *
 * Leave is checked at whole-day granularity (a date is either "on leave" or
 * not), not per-occurrence-time overlap like assertInstructorAvailable's own
 * leave check — a deliberate simplification that matches how leave is
 * actually used in this app (always a full-day date range in the Cuti UI),
 * not a general-purpose partial-day leave model.
 *
 * A candidate start before now — any date before today, or an earlier time
 * today — is a backfill of a class that already ran (admin-only backdated
 * schedules). Like assertInstructorAvailable, rules 1 and 2 skip it — the
 * instructor's CURRENT hours and leave say nothing about a session they
 * already taught — while rule 3 (busy windows) still applies.
 */
const computeForDates = (
  availability: InstructorAvailability,
  duration: number,
  dates: string[],
): AvailabilityDisabledTimeResult => {
  if (dates.length === 0 || !duration) {
    // No data yet (still loading, gate not satisfied, or a fetch error —
    // see the "fail open" edge case) — vacuously unrestricted, not a
    // failure state.
    return EMPTY_RESULT;
  }

  const {
    has_working_hours_configured: hasWorkingHoursConfigured,
    working_hours_by_weekday: workingHoursByWeekday,
    leave_ranges: leaveRanges,
    busy_windows_by_date: busyWindowsByDate,
  } = availability;

  // Impossible-regardless-of-time dates, computed once — reused both by the
  // per-candidate-time loop below and by the caller's message-building.
  const todayKey = now().format(DEFAULT_DATE_SUBMIT_FORMAT);
  // YYYY-MM-DD keys compare lexically in date order.
  const isPastDate = (date: string) => date < todayKey;
  // `t` is wall-clock minutes in the schedule timezone, like now().
  const nowMinutes = now().hour() * 60 + now().minute();
  const isBackfillSlot = (date: string, t: number) =>
    isPastDate(date) || (date === todayKey && t < nowMinutes);

  const impossibleDates: ImpossibleDate[] = [];
  dates.forEach((date) => {
    if (isPastDate(date)) return;
    const weekday = dateOnly(date).day();
    const noWorkingHours =
      hasWorkingHoursConfigured && !(workingHoursByWeekday[String(weekday)]?.length);
    if (noWorkingHours) {
      impossibleDates.push({ date, reason: "no_working_hours" });
      return;
    }
    const onLeave = leaveRanges.some(
      (range) => range.start_date <= date && date <= range.end_date,
    );
    if (onLeave) {
      impossibleDates.push({ date, reason: "on_leave" });
    }
  });
  const impossibleDateSet = new Set(impossibleDates.map((d) => d.date));

  const disabledMinutesByHour: Record<number, Set<number>> = {};
  const markDisabled = (totalMinutes: number) => {
    const hour = Math.floor(totalMinutes / 60);
    const minute = totalMinutes % 60;
    (disabledMinutesByHour[hour] ??= new Set()).add(minute);
  };

  for (let t = 0; t < MINUTES_IN_DAY; t += MINUTE_STEP) {
    const end = t + duration;
    let valid = true;

    for (const date of dates) {
      // Rules 1 and 2 don't apply to a backfill slot (see the doc comment);
      // an impossible TODAY still lets its earlier hours through.
      const backfill = isBackfillSlot(date, t);
      if (!backfill && impossibleDateSet.has(date)) {
        valid = false;
        break;
      }

      // 1. Working hours — three-state rule (see has_working_hours_configured's
      // doc comment on InstructorAvailability).
      if (hasWorkingHoursConfigured && !backfill) {
        const weekday = dateOnly(date).day();
        const blocks = workingHoursByWeekday[String(weekday)] ?? [];
        const fitsAnyBlock = blocks.some((block) => {
          const blockStart = hhmmToMinutes(block.start_time);
          const blockEnd = hhmmToMinutes(block.end_time);
          // Same inclusive comparisons assertInstructorAvailable itself uses
          // — not strict >/<, or a boundary-exact time that's genuinely fine
          // would be needlessly rejected here.
          return t >= blockStart && end <= blockEnd;
        });
        if (!fitsAnyBlock) {
          valid = false;
          break;
        }
      }

      // 2. Approved leave — already fully excluded via impossibleDateSet
      // above (whole-day granularity), nothing further to check per-time.

      // 3. Existing busy windows for this date.
      const busy = busyWindowsByDate[date] ?? [];
      const hasConflict = busy.some((window) => {
        const busyStart = hhmmToMinutes(window.start_time);
        const busyEnd = hhmmToMinutes(window.end_time);
        return t < busyEnd && end > busyStart;
      });
      if (hasConflict) {
        valid = false;
        break;
      }
    }

    if (!valid) markDisabled(t);
  }

  const slotsPerHour = 60 / MINUTE_STEP;
  const disabledHours = Object.keys(disabledMinutesByHour)
    .map(Number)
    .filter((hour) => (disabledMinutesByHour[hour]?.size ?? 0) >= slotsPerHour);

  return {
    disabledTime: () => ({
      disabledHours: () => disabledHours,
      disabledMinutes: (hour: number) => Array.from(disabledMinutesByHour[hour] ?? []),
    }),
    impossibleDates,
  };
};

/**
 * Given the instructor-availability preview response, computes which
 * candidate start times (in TIME_MINUTE_STEP increments across a 24h day)
 * are valid across EVERY date the proposed pattern touches — the shared
 * "Waktu Mulai" field's own grid, since one time value has to work for the
 * whole recurrence at once. See computeForDates for the underlying rules.
 */
export const buildAvailabilityDisabledTime = (
  availability: InstructorAvailability | undefined,
  duration: number,
): AvailabilityDisabledTimeResult => {
  if (!availability) return EMPTY_RESULT;
  return computeForDates(availability, duration, availability.dates);
};

/**
 * Narrows the same computation to just the occurrences of ONE weekday within
 * the pattern — for the "Waktu berbeda per hari" per-day pickers, where each
 * weekday gets its own independently-picked time and so needs its own
 * independent validity grid, rather than being folded into the aggregate
 * "must work for every date in the whole pattern" check above (which would
 * incorrectly let one blocked day's constraints leak into another day's
 * picker).
 */
export const buildAvailabilityDisabledTimeForWeekday = (
  availability: InstructorAvailability | undefined,
  duration: number,
  weekday: number, // 0=Sunday..6=Saturday, dayjs .day()
): AvailabilityDisabledTimeResult => {
  if (!availability) return EMPTY_RESULT;
  const datesForWeekday = availability.dates.filter(
    (date) => dateOnly(date).day() === weekday,
  );
  return computeForDates(availability, duration, datesForWeekday);
};
